/**
 * The app's six brand presets — Flame, Pulse, Indigo, Lime, Ocean, Raspberry — as colour data: each
 * one's gradient stops, its solid and the ink it writes on its own fill, and the one serializer for
 * its gradient.
 *
 * TRANSCRIBED from the handoff „Kolor marki — cztery pickery" and from the mobile app's
 * `src/design-system/brandColor.ts`, never re-derived: the two apps paint one trainer's colour the
 * same way.
 *
 * ── Why this is in `shared/` ─────────────────────────────────────────────────────────────────
 * It lived in `entities/brand-color/model/brandColor.ts` while the trainer's own app was its only
 * painter. The public form's Studio template (`entities/public-form`) is the second: a form in
 * „Pulse" is the same red as the trainer's app in „Pulse". An entity may not import another, so the
 * table moved down a layer on that second consumer, 27 September 2026. What a CHOICE is — the wire,
 * the save, the paint of `/konto` — stays in the brand-color entity; this file is only the palette.
 */

export type BrandPresetId = 'flame' | 'pulse' | 'indigo' | 'lime' | 'ocean' | 'raspberry';

export type BrandPresetWire = 'Flame' | 'Pulse' | 'Indigo' | 'Lime' | 'Ocean' | 'Raspberry';

export interface BrandPreset {
  readonly id: BrandPresetId;
  readonly wire: BrandPresetWire;
  readonly labelKey: string;
  /** The KANON 135° gradient's three stops, first → last. */
  readonly stops: readonly [string, string, string];
  /** Stop positions, 0–1, transcribed from the handoff's `linear-gradient` percentages. */
  readonly locations: readonly [number, number, number];
  /** The preset's SOLID accent — the swatch, a dot, a ring. */
  readonly solid: string;
  /**
   * Which ink the preset writes on its own fill. Authored per preset, not computed: a max-contrast
   * rule picks dark ink for Flame too, which the handoff does not.
   */
  readonly onTile: 'light' | 'dark';
}

export const BRAND_PRESETS: readonly BrandPreset[] = [
  {
    id: 'flame',
    wire: 'Flame',
    labelKey: 'profile.brandColor.presets.flame',
    stops: ['#ff8a3c', '#ff6b2c', '#ff3b1f'],
    locations: [0, 0.42, 1],
    solid: '#ff5a1f',
    onTile: 'light',
  },
  {
    id: 'pulse',
    wire: 'Pulse',
    labelKey: 'profile.brandColor.presets.pulse',
    stops: ['#ff5f5f', '#e01b32', '#8f0b22'],
    locations: [0, 0.48, 1],
    solid: '#e01b32',
    onTile: 'light',
  },
  {
    id: 'indigo',
    wire: 'Indigo',
    labelKey: 'profile.brandColor.presets.indigo',
    stops: ['#7b8cff', '#4c5ff5', '#2a2fd0'],
    locations: [0, 0.55, 1],
    solid: '#4c5ff5',
    onTile: 'light',
  },
  {
    id: 'lime',
    wire: 'Lime',
    labelKey: 'profile.brandColor.presets.lime',
    stops: ['#c8f24a', '#8fd41a', '#4f9e0a'],
    locations: [0, 0.5, 1],
    solid: '#8fd41a',
    onTile: 'dark',
  },
  {
    id: 'ocean',
    wire: 'Ocean',
    labelKey: 'profile.brandColor.presets.ocean',
    stops: ['#4fd6c9', '#16a9b8', '#0b6f8f'],
    locations: [0, 0.55, 1],
    solid: '#16a9b8',
    onTile: 'light',
  },
  {
    id: 'raspberry',
    wire: 'Raspberry',
    labelKey: 'profile.brandColor.presets.raspberry',
    stops: ['#ff7ab0', '#f0397c', '#b8145a'],
    locations: [0, 0.52, 1],
    solid: '#f0397c',
    onTile: 'light',
  },
];

export function brandPresetById(id: string): BrandPreset | undefined {
  return BRAND_PRESETS.find((preset) => preset.id === id);
}

export function brandPresetByWire(wire: string): BrandPreset | undefined {
  return BRAND_PRESETS.find((preset) => preset.wire === wire);
}

/**
 * A gradient's CSS: `stops` at `locations` (0 to 1), optionally turned by `angle`. One serializer
 * for the preview's 135deg sweep and the app's own vertical and 90deg ones.
 */
export function brandGradientCss(
  stops: readonly string[],
  locations: readonly number[],
  angle?: string,
): string {
  const body = stops
    .map((stop, index) => `${stop} ${String(Math.round((locations[index] ?? 0) * 100))}%`)
    .join(', ');
  return `linear-gradient(${angle === undefined ? '' : `${angle}, `}${body})`;
}
